import { SharedCommonUtility } from '../utils/common.utility';

const normalizeToKeyValue = (flag: string): [string, string | null] => {
  const trimmed = flag.trim();
  const withoutDashes = trimmed.startsWith('--') ? trimmed.substring(2) : trimmed;
  if (withoutDashes.length === 0) {
    return ['', null];
  }
  const eqIndex = withoutDashes.indexOf('=');
  if (eqIndex === -1) {
    return [withoutDashes.trim(), null];
  }
  const key = withoutDashes.substring(0, eqIndex).trim();
  const value = withoutDashes.substring(eqIndex + 1).trim();
  return [key, value];
};

export const normalizeToName = (flag: string): string => {
  const [key] = normalizeToKeyValue(flag);
  return key;
};

export const normalizeToPersisted = (flag: string): string => {
  const [key, value] = normalizeToKeyValue(flag);
  return value === null ? key : `${key}=${value}`;
};

export const splitBrowsingArgsCsv = (csv: string): string[] =>
  String(csv)
    .split(',')
    .map((arg: string): string => arg.trim())
    .filter((arg: string): boolean => arg.length > 0);

export function sanitizePersistedBrowserFlags(
  sourceFlags: ReadonlyArray<string>,
  requiredFlags: ReadonlyArray<string>,
): string[] {
  const requiredNames = new Set(requiredFlags.map(normalizeToName));
  const dedup = new Map<string, string>();

  for (const raw of sourceFlags ?? []) {
    const name = normalizeToName(raw);
    if (!name) {
      continue;
    }
    if (requiredNames.has(name)) {
      continue;
    }
    dedup.set(name, normalizeToPersisted(raw));
  }

  return Array.from(dedup.values());
}

const flagsToMap = (flags: ReadonlyArray<string>): Map<string, string | null> => {
  const map = new Map<string, string | null>();
  for (const flag of flags) {
    if (SharedCommonUtility.notNullish(flag) === false) {
      continue;
    }
    const [key, value] = normalizeToKeyValue(flag);
    if (key.length === 0) {
      continue;
    }
    map.set(key, value);
  }
  return map;
};

export interface MergeBrowserFlagsOptions {
  required: ReadonlyArray<string>;
  extras?: ReadonlyArray<string>;
}

export const mergeBrowserFlags = (tenantFlags: ReadonlyArray<string>, opts: MergeBrowserFlagsOptions): string[] => {
  const requiredMap = flagsToMap(opts.required);
  const merged = new Map<string, string | null>(requiredMap);

  const sourcesInPriority: Array<Map<string, string | null>> = [flagsToMap(opts.extras ?? []), flagsToMap(tenantFlags ?? [])];

  for (const src of sourcesInPriority) {
    for (const [k, v] of src) {
      const isRequired = requiredMap.has(k);
      if (!isRequired) {
        merged.set(k, v);
      }
    }
  }

  const result: string[] = Array.from<[string, string | null], string>(merged, ([k, v]: [string, string | null]): string =>
    v === null ? `--${k}` : `--${k}=${v}`,
  );
  return result.filter((f: string) => f !== '--');
};
